/**
 * Order list, order detail, and delivery confirmation.
 *
 * Three screens in one file because they share the status vocabulary and the
 * same data shapes.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  AuthImage,
  Banner, Button, Card, Chip, ChipRow, ConfirmSheet, Empty, ErrorState, Money,
  Sheet, Skeleton, Stepper, TextArea, useToast,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus, useOnline, usePoll } from '../../lib/useApi';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { api, ApiError, mediaUrl, openPdf } from '../../lib/api';
import * as outbox from '../../lib/outbox';
import { dateShort, hoursUntil, money, relative, timeShort, piecesAndPacks, telLink } from '../../lib/format';
import { statusTone } from './Home';
import { IconChevron, IconPhone, IconPin } from '../../components/Icons';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

interface OrderRow {
  id: string;
  order_number: string;
  order_date: string;
  status: string;
  payment_mode: string;
  net_amount_paise: number;
  order_source: string;
  credit_limit_overridden: number;
  customer_name: string;
  customer_code: string;
  shop_name: string;
  invoice_id: string | null;
  invoice_number: string | null;
  payment_status: string | null;
  balance_amount_paise: number | null;
  salesperson_code: string | null;
  dispatch_state: string | null;
  dispatch_allocation: 'beat' | 'godown' | null;
  assigned_driver_name: string | null;
}

type Filter = 'today' | 'month' | 'active' | 'delivered' | 'cancelled';

/**
 * The dispatch allocation, as a chip shown wherever an order is listed — the
 * same vocabulary across the app. Only for an order still in play (not
 * delivered/cancelled/closed); a finished order's dispatch is history.
 */
function dispatchChip(o: {
  status: string;
  dispatch_state: string | null;
  dispatch_allocation: 'beat' | 'godown' | null;
  assigned_driver_name: string | null;
}): { label: string; tone: 'ok' | 'warn' | undefined } | null {
  if (['delivered', 'closed', 'cancelled'].includes(o.status)) return null;
  if (o.dispatch_allocation === 'godown') return { label: 'Godown pickup', tone: 'warn' };
  if (o.dispatch_allocation === 'beat' || o.dispatch_state === 'assigned') {
    return {
      label: o.assigned_driver_name ? `On ${o.assigned_driver_name}` : 'Queued to beat',
      tone: 'ok',
    };
  }
  return { label: 'To dispatch', tone: 'warn' };
}

export function OrderList() {
  const t = useT();
  const navigate = useNavigate();
  const { isStaff, user } = useAuth();
  // The chip to open on, from `?view=`, so a dashboard tile can land on the
  // right one — "Orders today" opens Today's orders, not the month default.
  const [searchParams] = useSearchParams();
  const initialFilter: Filter = (['today', 'month', 'active', 'delivered', 'cancelled'] as const)
    .includes(searchParams.get('view') as Filter)
    ? (searchParams.get('view') as Filter)
    : 'month';
  const [filter, setFilter] = useState<Filter>(initialFilter);

  // Mirrors the server's grants for `order.create_on_behalf`; manager and
  // accountant hold no such permission and would be refused at submit.
  const mayOrderForCustomer = user?.role === 'owner_admin'
    || user?.role === 'salesperson'
    || user?.role === 'driver'
    || user?.role === 'field_agent';
  const isField = user?.role === 'salesperson' || user?.role === 'driver' || user?.role === 'field_agent';
  const isAdmin = user?.role === 'owner_admin' || user?.role === 'manager' || user?.role === 'accountant';
  const isDriver = user?.role === 'driver' || user?.role === 'field_agent';

  const qaStyle: CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 7, alignItems: 'flex-start',
    padding: 14, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'var(--ink)',
  };
  const qaIcon: CSSProperties = {
    width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 17,
  };
  const badgeStyle = (bg: string, color: string): CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700,
    padding: '2px 8px', borderRadius: 999, background: bg, color, whiteSpace: 'nowrap',
  });

  // Today / month as date prefixes — used both for the server-side list filter
  // and for building the chip date ranges.
  const pad = (n: number) => String(n).padStart(2, '0');
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const monthStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;

  // The list is filtered on the SERVER per chip, so the rows are the whole set
  // for that chip — not a client-side slice of the latest 100, which undercounted
  // and truncated once a month passed 100 orders.
  const listUrl = (() => {
    const base = '/api/orders?pageSize=100';
    switch (filter) {
      case 'today': return `${base}&fromDate=${todayStr}&toDate=${todayStr}`;
      case 'month': return `${base}&fromDate=${monthStr}-01&toDate=${todayStr}`;
      case 'active': return `${base}&activeOnly=true`;
      case 'delivered': return `${base}&deliveredTodayOnly=true`;
      case 'cancelled': return `${base}&status=cancelled`;
      default: return base;
    }
  })();
  const { data, loading, error, refetch } = useQuery<{ items: OrderRow[]; total: number }>(listUrl, {
    refreshOn: ['orders', 'deliveries'],
  });
  useRefreshOnFocus(refetch);

  // Chip counts, computed authoritatively in SQL (role-scoped), not from a page.
  const countsQ = useQuery<{
    total: number; today: number; month: number;
    active: number; cancelled: number; deliveredToday: number;
  }>('/api/orders/counts');
  useRefreshOnFocus(countsQ.refetch);
  const counts = countsQ.data;

  // Today's collection split and customer activity, so the Collect and
  // Customer-ledger tiles mirror the dashboard.
  const dayQ = useQuery<{ progress?: {
    collectionsCount?: number;
    collectionsAmountPaise?: number;
    collectionsCashPaise?: number; collectionsUpiPaise?: number;
    collectionsAdvancePaise?: number; collectionsBalancePaise?: number;
    customersAdded?: number; customersUpdated?: number;
  } }>(
    isField ? '/api/field/day/current' : null,
  );
  const cashPaise = dayQ.data?.progress?.collectionsCashPaise ?? 0;
  const upiPaise = dayQ.data?.progress?.collectionsUpiPaise ?? 0;
  const dp = dayQ.data?.progress;

  /**
   * Today's customer-ledger counts for the tile, so it is live for EVERY staff
   * role, not only the field.
   *
   * Field staff (salesperson, driver) have their own figures in the day
   * progress above — what THEY added or edited today. An admin has no field
   * "day", so the tile read blank for them; it now takes the whole-force
   * figures from the same ledger endpoint the Customer-ledger screen uses, the
   * way the admin dashboard tile does.
   */
  const ledgerQ = useQuery<{ new: { id: string }[]; updated: { id: string }[] }>(
    isStaff && !isField ? '/api/customers/ledger-activity' : null,
  );
  const ledgerNew = isField ? (dp?.customersAdded ?? 0) : (ledgerQ.data?.new.length ?? 0);
  const ledgerUpdated = isField ? (dp?.customersUpdated ?? 0) : (ledgerQ.data?.updated.length ?? 0);

  // Orders routed to this driver and still to deliver — the count on the tile.
  const toDeliverQ = useQuery<{ items: { id: string }[] }>(
    isDriver ? '/api/orders/assigned' : null,
  );
  usePoll(toDeliverQ.refetch, 15000, isDriver);
  const toDeliverCount = toDeliverQ.data?.items.length ?? 0;

  // The server already returned exactly the rows for the active chip.
  const rows = data?.items ?? [];

  return (
    <Page
      title={t('order.title')}
      // The way in to placing an order.
      //
      // This screen had no create action at all, for any role, which made the
      // whole order flow unreachable from the admin app — the only screen that
      // posts an order is the cart, and nothing linked to it. A customer
      // reaches the catalogue from their own navigation. The header "+ New
      // order" action was removed — the quick-action tile below is the one way
      // in, so there is no duplicate control.
    >
      <div className="stack">
        {/* Quick actions — staff only. Each tile is shown only when its
            destination is one the viewer's role can actually open. */}
        {isStaff && (mayOrderForCustomer || isField || isAdmin) && (
          <div className="grid-2">
            {mayOrderForCustomer && (
              <button type="button" className="card" style={qaStyle} onClick={() => navigate('/customers')}>
                <span style={{ ...qaIcon, background: '#eaf0ff' }}>📦</span>
                <span className="t-sm t-semi">New order / Invoice</span>
                <span className="row" style={{ marginTop: 2 }}>
                  <span style={badgeStyle('var(--accent-bg, #eaf0ff)', 'var(--accent, #2b64f5)')}>
                    Total orders: {counts?.total ?? 0}
                  </span>
                </span>
              </button>
            )}
            {isField && (
              <button type="button" className="card" style={qaStyle} onClick={() => navigate('/collections')}>
                <span style={{ ...qaIcon, background: '#eafaf1' }}>💵</span>
                <span className="t-sm t-semi">Collect cash / UPI</span>
                {/* Full breakdown, mirroring the dashboard Collections tile:
                    receipts, Cash, UPI, Balance owed, Advance held. */}
                <span className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                  <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>Cash: {money(cashPaise)}</span>
                  <span style={badgeStyle('#eaf0ff', 'var(--accent, #2b64f5)')}>UPI: {money(upiPaise)}</span>
                  <span style={badgeStyle(
                    (dp?.collectionsBalancePaise ?? 0) > 0 ? 'var(--warn-bg, #fff5e6)' : 'var(--line-2)',
                    (dp?.collectionsBalancePaise ?? 0) > 0 ? 'var(--warn, #c08400)' : 'var(--ink-2)',
                  )}>
                    Balance: {money(dp?.collectionsBalancePaise ?? 0)}
                  </span>
                  <span style={badgeStyle('#eef2ff', 'var(--accent, #2b64f5)')}>
                    Advance: {money(dp?.collectionsAdvancePaise ?? 0)}
                  </span>
                </span>
              </button>
            )}
            {/*
              CUSTOMER LEDGER — live for every staff role now, not only the
              field. The two badges (new / updated today) always show; the
              figures are the viewer's own day for field staff and the whole
              force for an admin. An admin opens the full ledger screen; field
              staff, who cannot open that admin-only screen, open the customer
              list as before.
            */}
            <button
              type="button"
              className="card"
              style={qaStyle}
              onClick={() => navigate(isAdmin ? '/customer-ledger' : '/customers')}
            >
              <span style={{ ...qaIcon, background: '#f0ecfb' }}>📋</span>
              <span className="t-sm t-semi">Customer ledger</span>
              <span className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>New customers {ledgerNew}</span>
                <span style={badgeStyle('var(--line-2)', 'var(--ink-2)')}>Updated {ledgerUpdated}</span>
              </span>
            </button>
            {/* Orders routed to this driver, still to be delivered. Opens the
                full list; each order taps through to its delivery (POD). */}
            {isDriver && (
              <button
                type="button"
                className="card"
                style={qaStyle}
                onClick={() => navigate('/orders-to-deliver')}
              >
                <span style={{ ...qaIcon, background: '#eafaf1' }}>🚚</span>
                <span className="t-sm t-semi">Orders Pending for Delivery</span>
                <span className="row" style={{ marginTop: 2 }}>
                  <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>
                    Pending: {toDeliverCount}
                  </span>
                </span>
              </button>
            )}
          </div>
        )}

        <ChipRow
          value={filter}
          onChange={setFilter}
          options={[
            // Count embedded in the label so every pill shows it, including a
            // zero (ChipRow's own count badge hides a zero).
            { value: 'today', label: `Today's orders · ${counts?.today ?? 0}` },
            { value: 'month', label: `Total orders in this Month · ${counts?.month ?? 0}` },
            { value: 'active', label: `${t('order.out_for_delivery')} · ${counts?.active ?? 0}` },
            // The sorter label the client renamed on 23 Oct. Kept as a literal
            // rather than the `order.delivered` i18n key, because that key also
            // labels each order's status badge — and a badge on a week-old
            // order must still read "Delivered", not "Orders Delivered Today".
            // Count is now truly today-scoped (deliveries recorded today).
            { value: 'delivered', label: `Orders Delivered Today · ${counts?.deliveredToday ?? 0}` },
            { value: 'cancelled', label: `${t('order.cancelled')} · ${counts?.cancelled ?? 0}` },
          ]}
        />

        {loading && !data && <Skeleton rows={5} height={78} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}
        {data && rows.length === 0 && <Empty />}

        <Card flush>
          <div className="list">
            {rows.map((o) => (
              <button
                key={o.id}
                className="list__item"
                onClick={() => navigate(`/orders/${o.id}`)}
                style={{ alignItems: 'flex-start', gap: 10 }}
              >
                <div className="grow" style={{ minWidth: 0 }}>
                  {/* Row 1 — order id + status badges (wrap, never overflow) */}
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className="t-semi t-sm">{o.order_number}</span>
                    <Chip tone={statusTone(o.status)}>{t(`order.${o.status}`)}</Chip>
                    {isStaff && (() => {
                      const d = dispatchChip(o);
                      return d ? <Chip tone={d.tone}>{d.label}</Chip> : null;
                    })()}
                    {o.credit_limit_overridden === 1 && <Chip tone="warn">Over limit</Chip>}
                  </div>
                  {/* Row 2 — customer name, free to wrap to a second line */}
                  {isStaff && o.customer_name && (
                    <div className="t-sm" style={{ overflowWrap: 'anywhere', marginTop: 3 }}>
                      {o.customer_name}
                    </div>
                  )}
                  {/* Row 3 — date, invoice, and any balance */}
                  <div className="t-xs t-muted" style={{ overflowWrap: 'anywhere', marginTop: 2 }}>
                    {dateShort(o.order_date)}
                    {o.invoice_number ? ` · ${o.invoice_number}` : ''}
                    {o.balance_amount_paise !== null && o.balance_amount_paise > 0 && (
                      <span className="t-warn"> · {t('ledger.balance')} {money(o.balance_amount_paise)}</span>
                    )}
                  </div>
                </div>
                {/* Total, aligned to the right */}
                <div className="row" style={{ gap: 4, alignItems: 'center', flex: '0 0 auto' }}>
                  <Money paise={o.net_amount_paise} className="t-sm t-semi" />
                  <IconChevron width={18} height={18} style={{ color: 'var(--faint)' }} />
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

interface OrderDetailData {
  order: Record<string, unknown> & {
    id: string; order_number: string; order_date: string; status: string;
    is_locked: number; payment_mode: string; net_amount_paise: number;
    gross_amount_paise: number; discount_amount_paise: number; taxable_amount_paise: number;
    cgst_amount_paise: number; sgst_amount_paise: number; igst_amount_paise: number;
    cess_amount_paise: number; round_off_paise: number;
    requested_delivery_date: string | null; confirmed_delivery_date: string | null;
    reschedule_reason: string | null; cancellation_reason: string | null;
    customer_name: string; shop_name: string; address_line1: string;
    latitude: number | null; longitude: number | null; shop_phone: string | null;
    invoice_id: string | null; invoice_number: string | null;
    payment_status: string | null; paid_amount_paise: number | null;
    balance_amount_paise: number | null; due_date: string | null;
    dispatch_state: string | null; dispatch_allocation: 'beat' | 'godown' | null;
    assigned_driver_name: string | null; beat_name: string | null;
  };
  items: {
    line_no: number; sku_code: string; product_name: string; uom: string;
    ordered_quantity: number; confirmed_quantity: number | null; free_quantity: number;
    // base_quantity is ALWAYS pieces; ordered/confirmed are in sold_uom (PCS/BOX).
    sold_uom: string; base_quantity: number | null;
    rate_paise: number; net_amount_paise: number;
    /** From the product. Null when BC holds no picture, or the item is gone. */
    image_r2_key: string | null;
    bc_pack_uom: string | null; bc_pack_qty: number | null;
  }[];
  dispatch: Record<string, unknown> | null;
  delivery: {
    delivered_at: string; outcome: string; driver_declared_quantity: number;
    customer_confirmed: number; customer_confirmed_at: string | null;
    auto_confirmed: number; auto_confirm_due_at: string | null;
    photo_r2_key: string | null; has_quantity_mismatch: number;
  } | null;
}

// ---------------------------------------------------------------------------
// Orders to deliver — the driver's list of routed, undelivered orders
// ---------------------------------------------------------------------------

interface DeliverRow {
  id: string;
  order_number: string;
  invoice_id: string | null;
  invoice_number: string | null;
  legal_name: string | null;
  trade_name: string | null;
  shop_name: string | null;
  address_line1: string | null;
  balance_amount_paise: number | null;
  net_amount_paise: number;
}

/**
 * The orders routed to this driver and not yet delivered (GET
 * /api/orders/assigned). Opened from the "Orders to Deliver" tile on the Orders
 * screen. Each row taps through to the direct POD to mark it delivered. Polled
 * so a newly routed order appears while the screen is open.
 */
export function OrdersToDeliver() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery<{ items: DeliverRow[] }>(
    '/api/orders/assigned',
    { refreshOn: ['orders', 'deliveries', 'dispatches'] },
  );
  useRefreshOnFocus(refetch);
  usePoll(refetch, 15000);

  return (
    <Page title="Orders Pending for Delivery" back>
      <div className="stack">
        {loading && !data && <Skeleton rows={4} height={72} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}

        {data && data.items.length === 0 && (
          <Empty
            title="Nothing to deliver yet"
            hint="Orders for your beats appear here once your day is open."
          />
        )}

        {data && data.items.length > 0 && (
          <Card flush>
            <div className="list">
              {data.items.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="list__item"
                  disabled={!o.invoice_id}
                  onClick={() => { if (o.invoice_id) navigate(`/pod/direct/${o.invoice_id}`); }}
                >
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="t-semi t-clip">{o.trade_name || o.legal_name || o.shop_name}</div>
                    <div className="t-xs t-muted t-clip">
                      {o.shop_name}{o.address_line1 ? ` · ${o.address_line1}` : ''}
                    </div>
                    <div className="t-xs t-muted">{o.invoice_number ?? o.order_number}</div>
                  </div>
                  {/* The order's VALUE, not its outstanding balance — a paid
                      order still shows what it is worth, not ₹0. */}
                  <Money paise={o.net_amount_paise} className="t-sm" />
                </button>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}

export function OrderDetail() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isStaff } = useAuth();

  const { data, loading, error, refetch } = useQuery<OrderDetailData>(id ? `/api/orders/${id}` : null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (loading && !data) return <Page title={t('order.number')} back><Skeleton rows={4} /></Page>;
  if (error && !data) {
    return <Page title={t('order.number')} back><ErrorState message={error.message} onRetry={refetch} /></Page>;
  }
  if (!data) return <Page title={t('order.number')} back><Empty /></Page>;

  const o = data.order;
  // The cut-off agreed with the client: editable and cancellable right up to
  // out_for_delivery, and locked from that point.
  const cancellable = ['placed', 'confirmed', 'invoiced', 'dispatched'].includes(o.status)
    && o.is_locked !== 1;

  /**
   * May this order be delivered by hand, right now?
   *
   * Invoiced but not yet delivered, and NOT on a route. `data.dispatch` is the
   * test for the second: an order on a load sheet belongs to a driver's round,
   * where the van stock and the stop sequence live, and it is delivered from
   * there. Offering the direct button as well would invite two people to
   * deliver the same invoice — the server refuses the second, but a button that
   * exists only to be refused is a button that should not exist.
   */
  const canDeliverDirect = !data.delivery
    && !data.dispatch
    && ['invoiced', 'confirmed'].includes(o.status);

  async function cancel() {
    setBusy(true);
    try {
      const result = await api.post<{ creditNoteNumber: string | null; note: string }>(
        `/api/orders/${id}/cancel`, { reason: cancelReason.trim() || undefined },
      );
      toast.show(
        result.creditNoteNumber
          ? `Cancelled · credit note ${result.creditNoteNumber}`
          : 'Cancelled',
        'ok',
      );
      setCancelOpen(false);
      void refetch();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title={o.order_number} subtitle={t(`order.${o.status}`)} back="/orders">
      <div className="stack">
        {/* Dispatch allocation — the same status the dispatch queue and the
            order list show, so it reads consistently everywhere. */}
        {isStaff && (() => {
          const d = dispatchChip(o);
          if (!d) return null;
          return (
            <Card flush>
              <div className="list__item list__item--static" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="t-sm t-semi grow">Dispatch</span>
                <Chip tone={d.tone}>{d.label}</Chip>
                {o.beat_name && <span className="t-xs t-muted">Beat: {o.beat_name}</span>}
              </div>
            </Card>
          );
        })()}

        {o.status === 'cancelled' && (
          <Banner tone="danger" title={t('order.cancelled')}>
            {o.cancellation_reason && <span className="t-sm">{o.cancellation_reason}</span>}
          </Banner>
        )}

        {/* Admin rescheduled the requested slot — the customer should see why. */}
        {o.confirmed_delivery_date && o.confirmed_delivery_date !== o.requested_delivery_date && (
          <Banner tone="info" title="Delivery rescheduled">
            <span className="t-sm">
              Now {dateShort(o.confirmed_delivery_date)}
              {o.reschedule_reason && ` · ${o.reschedule_reason}`}
            </span>
          </Banner>
        )}

        <StatusTimeline status={o.status} delivery={data.delivery} />

        {/* The confirmation prompt only appears while the window is open. */}
        {data.delivery
          && data.delivery.customer_confirmed === 0
          && data.delivery.auto_confirmed === 0
          && !isStaff && (
          <Card>
            <div className="stack-2">
              <div className="t-semi">{t('delivery.confirmTitle')}</div>
              <div className="t-sm t-muted">
                {t('delivery.driverSaid', { n: data.delivery.driver_declared_quantity })}
              </div>
              {data.delivery.auto_confirm_due_at && hoursUntil(data.delivery.auto_confirm_due_at) > 0 && (
                <div className="t-xs t-warn">
                  {t('delivery.autoConfirmIn', { hours: hoursUntil(data.delivery.auto_confirm_due_at) })}
                </div>
              )}
              <Button block onClick={() => navigate(`/deliveries/${(data.delivery as { id?: string }).id ?? ''}/confirm`)}>
                {t('delivery.confirmReceipt')}
              </Button>
            </div>
          </Card>
        )}

        <Card flush>
          <div className="card__header"><h3>{o.shop_name}</h3></div>
          <div className="card__body stack-2">
            <span className="t-sm t-muted">{o.address_line1}</span>
            <div className="row" style={{ gap: 8 }}>
              {/* telLink, not a raw tel: — a shop with no number on file
                  holds a placeholder, and dialling that rings a stranger. */}
              {telLink(o.shop_phone) && (
                <a className="btn btn--ghost btn--sm" href={telLink(o.shop_phone)!}>
                  <IconPhone width={15} height={15} /> {t('app.call')}
                </a>
              )}
              {o.latitude && o.longitude && (
                <a
                  className="btn btn--ghost btn--sm"
                  href={`https://www.google.com/maps/search/?api=1&query=${o.latitude},${o.longitude}`}
                  target="_blank" rel="noreferrer"
                >
                  <IconPin width={15} height={15} /> {t('app.directions')}
                </a>
              )}
            </div>
          </div>
        </Card>

        <Card flush>
          <div className="card__header"><h3>{t('cart.title')}</h3></div>
          <div className="list">
            {data.items.map((item) => (
              <div key={item.line_no} className="list__item list__item--static">
                {/*
                  The product picture, on the screen a retailer and a driver
                  both open to check what was ordered against what is in the
                  crate. Recognising a bottle is faster than reading four
                  near-identical names.
                */}
                {item.image_r2_key && (
                  <img
                    src={mediaUrl(`/api/media/${encodeURIComponent(item.image_r2_key)}`)}
                    alt=""
                    className="thumb--sm"
                    loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="t-sm t-semi t-clip">{item.product_name}</div>
                  <div className="t-xs t-muted">
                    {item.ordered_quantity} {item.uom.toLowerCase()} × <Money paise={item.rate_paise} />
                    {item.free_quantity > 0 && ` · +${item.free_quantity} ${t('cart.free')}`}
                    {item.confirmed_quantity !== null
                      && item.confirmed_quantity !== item.ordered_quantity
                      && ` · confirmed ${item.confirmed_quantity}`}
                  </div>
                  {/* The same quantity in boxes + loose pieces, for the person
                      counting crates. Off base_quantity (pieces), NOT the sold-uom
                      ordered/confirmed figure — passing boxes here would read a
                      4-box line as "4 pcs". */}
                  {item.bc_pack_qty && item.bc_pack_qty > 1 && item.base_quantity != null && (
                    <div className="t-xs t-faint">
                      {piecesAndPacks(item.base_quantity, {
                        baseUom: item.uom, packUom: item.bc_pack_uom, packQty: item.bc_pack_qty,
                      })}
                    </div>
                  )}
                </div>
                <Money paise={item.net_amount_paise} className="t-sm" />
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="stack-2">
            <Line label={t('cart.gross')} paise={o.gross_amount_paise} />
            {o.discount_amount_paise > 0 && (
              <Line label={t('cart.discount')} paise={-o.discount_amount_paise} tone="ok" />
            )}
            <Line label={t('cart.taxable')} paise={o.taxable_amount_paise} />
            {o.cgst_amount_paise > 0 && <Line label="CGST" paise={o.cgst_amount_paise} />}
            {o.sgst_amount_paise > 0 && <Line label="SGST" paise={o.sgst_amount_paise} />}
            {o.igst_amount_paise > 0 && <Line label="IGST" paise={o.igst_amount_paise} />}
            {o.cess_amount_paise > 0 && <Line label={t('cart.cess')} paise={o.cess_amount_paise} />}
            {o.round_off_paise !== 0 && <Line label={t('cart.roundOff')} paise={o.round_off_paise} />}
            <hr className="divider" />
            <div className="row-between">
              <span className="t-bold">{t('cart.payable')}</span>
              <Money paise={o.net_amount_paise} className="t-lg" />
            </div>
            {o.paid_amount_paise !== null && o.paid_amount_paise > 0 && (
              <>
                <Line label={t('ledger.paid')} paise={o.paid_amount_paise} tone="ok" />
                <div className="row-between">
                  <span className="t-semi">{t('ledger.balance')}</span>
                  <Money paise={o.balance_amount_paise ?? 0} tone="danger" />
                </div>
              </>
            )}
          </div>
        </Card>

        <div className="stack-2">
          {o.invoice_id && (
            // Was an <a href> to the PDF route, which requires a bearer token
            // an anchor cannot send — so this returned 401 for every retailer
            // who ever tapped it. openPdf fetches with auth and hands the
            // browser a blob.
            <Button
              variant="ghost" block
              onClick={() => void openPdf(`/api/invoices/${o.invoice_id}/pdf`)}
            >
              {t('order.viewInvoice')} · {o.invoice_number}
            </Button>
          )}

          {/*
            DELIVER IT NOW — the on-call path.

            Shown to field staff on an invoiced order that has not been
            delivered and is not on a route. An order with a dispatch belongs to
            a driver's round and is delivered from the route screen, where the
            van stock and the stop sequence are; offering both would let the
            same invoice be delivered twice by two different people, which the
            server refuses but which should never be offered in the first place.

            Client rule, 29 Sep: a salesperson and a driver both do this.
          */}
          {isStaff && o.invoice_id && canDeliverDirect && (
            <Button
              block
              onClick={() => navigate(`/pod/direct/${o.invoice_id}`, {
                state: {
                  shopName: o.shop_name,
                  invoiceNumber: o.invoice_number,
                  customerName: o.customer_name,
                },
              })}
            >
              {t('pod.deliverNow')}
            </Button>
          )}

          {/*
            Staff only, and only the three-copy set.
            Rule 48(1) wants the invoice in three marked copies where goods
            move: one for the office, one travelling with the driver, one for
            the retailer. One document with all three is one print job — three
            separate downloads is three print dialogs and three chances to
            hand the retailer the transporter copy.

            The server refuses `copy=all` for a customer role, so this is not
            the only thing keeping them apart.
          */}
          {isStaff && o.invoice_id && (
            <Button
              variant="ghost" block
              onClick={() => void openPdf(`/api/invoices/${o.invoice_id}/pdf?copy=all`)}
            >
              {t('order.printAllCopies')}
            </Button>
          )}
          {['delivered', 'partially_delivered'].includes(o.status) && o.invoice_id && (
            <Button variant="ghost" block onClick={() => navigate(`/returns/new/${o.invoice_id}`)}>
              {t('order.raiseReturn')}
            </Button>
          )}
          {cancellable && (
            <Button variant="ghost" block onClick={() => setCancelOpen(true)}>
              {t('order.cancelOrder')}
            </Button>
          )}
          {!cancellable && o.status !== 'cancelled' && (
            <Banner tone="info"><span className="t-xs">{t('order.cannotCancel')}</span></Banner>
          )}
        </div>

        <OrderToCash orderId={id} />
      </div>

      <Sheet
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t('order.cancelOrder')}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setCancelOpen(false)} disabled={busy}>
              {t('app.cancel')}
            </Button>
            <Button variant="danger" block busy={busy} onClick={() => void cancel()}>
              {t('app.confirm')}
            </Button>
          </>
        }
      >
        <div className="stack-2">
          <TextArea
            label={t('order.cancelReason')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            maxLength={300}
          />
          {/* A GST invoice cannot be deleted — it is reversed by a credit note.
              Saying so avoids a support call asking why the invoice is still
              visible. */}
          <Banner tone="info">
            <span className="t-xs">
              A credit note will be issued against the invoice. Under GST an issued
              invoice cannot be deleted, only reversed.
            </span>
          </Banner>
        </div>
      </Sheet>
    </Page>
  );
}

/**
 * The order-to-cash trail: order placed through to cash settled.
 *
 * WHY IT LIVES HERE AND NOT IN REPORTS
 *
 * GET /api/reports/financial/order-to-cash/:orderId was built with the report
 * module and no screen ever called it — the eighth of eight finished reports
 * that were unreachable. It is the only one that is not a list: it answers
 * "what happened to THIS order", so a tab on the Reports screen would have
 * needed an order picker to reach the order the user is already looking at.
 *
 * WHY IT IS LAZY
 *
 * It runs six queries. Every retailer opening their own order would pay for
 * them, so it is fetched only when opened, and only for the roles that may
 * see it at all.
 *
 * WHO SEES IT
 *
 * `report.financial` is granted to owner_admin (which holds every permission)
 * and accountant. NOT manager, and not salesperson — both are staff, so
 * `isStaff` would have been the wrong gate and would have shown them a panel
 * that 403s. The roles are named here because the client has no permission
 * list to consult; if that grant changes, this list has to change with it,
 * which is why the test pins the two together.
 */
function OrderToCash({ orderId }: { orderId: string | undefined }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const maySee = user?.role === 'owner_admin' || user?.role === 'accountant';
  // Null path means useQuery does not fetch — this is what makes it lazy.
  const { data, loading, error } = useQuery<{
    invoice: { invoice_number: string; payment_status: string } | null;
    payments: { amount_paise: number; payment_mode: string; received_at: string }[];
    creditNotes: { credit_note_number: string; total_amount_paise: number }[];
    timeline: { at: string; event: string; detail?: string }[];
    cashRealised: boolean;
  }>(maySee && open && orderId ? `/api/reports/financial/order-to-cash/${orderId}` : null);

  if (!maySee) return null;

  if (!open) {
    return (
      <Button variant="ghost" block onClick={() => setOpen(true)}>
        Order-to-cash trail
      </Button>
    );
  }

  return (
    <Card flush>
      <div className="card__header"><h3>Order to cash</h3></div>
      <div className="card__body stack-2">
        {loading && <Skeleton rows={3} />}
        {error && <span className="t-sm t-danger">{error.message}</span>}
        {data && (
          <>
            {/*
              Whether the money actually arrived, said in one line. An order
              can be delivered, invoiced and still unpaid, and the status
              chips above do not distinguish those.
            */}
            <Banner tone={data.cashRealised ? 'info' : 'warn'}>
              <span className="t-sm">
                {data.cashRealised
                  ? 'Paid in full — cash realised.'
                  : data.invoice
                    ? `Invoiced, not fully paid (${data.invoice.payment_status}).`
                    : 'Not yet invoiced, so no cash is due.'}
              </span>
            </Banner>

            {data.timeline.length === 0 && (
              <span className="t-sm t-muted">Nothing recorded yet.</span>
            )}

            {data.timeline.map((step, i) => (
              <div key={`${step.at}-${i}`} className="row-between">
                <div style={{ minWidth: 0 }}>
                  <div className="t-sm t-semi t-clip">{step.event}</div>
                  {step.detail && <div className="t-xs t-muted t-clip">{step.detail}</div>}
                </div>
                <span className="t-xs t-muted" style={{ whiteSpace: 'nowrap' }}>
                  {dateShort(step.at)}
                </span>
              </div>
            ))}

            {data.creditNotes.length > 0 && (
              <>
                <hr className="divider" />
                {data.creditNotes.map((cn) => (
                  <div key={cn.credit_note_number} className="row-between">
                    <span className="t-sm">Credit note {cn.credit_note_number}</span>
                    <Money paise={cn.total_amount_paise} tone="warn" />
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function Line({ label, paise, tone }: { label: string; paise: number; tone?: 'ok' }) {
  return (
    <div className="row-between t-sm">
      <span className="t-muted">{label}</span>
      <Money paise={paise} tone={tone} />
    </div>
  );
}

/** Compact progress trail. Cheaper to read than a status word alone. */
function StatusTimeline({
  status, delivery,
}: {
  status: string;
  delivery: OrderDetailData['delivery'];
}) {
  const t = useT();
  const steps = ['placed', 'invoiced', 'dispatched', 'out_for_delivery', 'delivered'];
  const currentIndex = status === 'cancelled' ? -1
    : status === 'partially_delivered' ? 4
    : Math.max(0, steps.indexOf(status === 'confirmed' ? 'placed' : status));

  if (status === 'cancelled') return null;

  return (
    <Card>
      <div className="row" style={{ gap: 0, alignItems: 'flex-start' }}>
        {steps.map((step, i) => {
          const done = i <= currentIndex;
          return (
            <div key={step} className="grow" style={{ textAlign: 'center', position: 'relative' }}>
              {i > 0 && (
                <div
                  style={{
                    position: 'absolute', top: 9, left: 0, right: '50%', height: 2,
                    background: done ? 'var(--ok)' : 'var(--line)',
                  }}
                  aria-hidden
                />
              )}
              {i < steps.length - 1 && (
                <div
                  style={{
                    position: 'absolute', top: 9, left: '50%', right: 0, height: 2,
                    background: i < currentIndex ? 'var(--ok)' : 'var(--line)',
                  }}
                  aria-hidden
                />
              )}
              <div
                style={{
                  width: 20, height: 20, borderRadius: '50%', margin: '0 auto',
                  background: done ? 'var(--ok)' : 'var(--surface)',
                  border: `2px solid ${done ? 'var(--ok)' : 'var(--line)'}`,
                  position: 'relative', zIndex: 1,
                  display: 'grid', placeItems: 'center',
                  color: '#fff', fontSize: 11, fontWeight: 700,
                }}
                aria-hidden
              >{done ? '✓' : ''}</div>
              <div className="t-xs" style={{ marginTop: 6, color: done ? 'var(--ink)' : 'var(--faint)' }}>
                {t(`order.${step}`)}
              </div>
            </div>
          );
        })}
      </div>

      {delivery && (
        <div className="t-xs t-muted" style={{ marginTop: 12, textAlign: 'center' }}>
          {timeShort(delivery.delivered_at)} · {delivery.driver_declared_quantity} delivered
          {delivery.customer_confirmed === 1 && ` · ${t('delivery.confirmed')}`}
          {delivery.auto_confirmed === 1 && ' · auto-confirmed'}
          {delivery.has_quantity_mismatch === 1 && ` · ${t('delivery.mismatchNote')}`}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Delivery confirmation — element three of the three-part POD
// ---------------------------------------------------------------------------

interface PendingDelivery {
  id: string;
  delivered_at: string;
  driver_declared_quantity: number;
  auto_confirm_due_at: string;
  photo_r2_key: string | null;
  invoice_number: string;
  shop_name: string;
  driver_name: string;
  lines: {
    id: string; sku_code: string; product_name: string;
    invoiced_quantity: number; driver_delivered_quantity: number; driver_shortage_quantity: number;
  }[];
}

export function ConfirmDelivery() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();

  const { data, loading, refetch } = useQuery<{ items: PendingDelivery[] }>(
    '/api/deliveries/pending-confirmation',
  );

  const delivery = data?.items.find((d) => d.id === id) ?? null;
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [dispute, setDispute] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!delivery) return;
    // Default to the driver's numbers so the common case — everything correct —
    // is a single tap.
    setQuantities(Object.fromEntries(
      delivery.lines.map((l) => [l.id, l.driver_delivered_quantity]),
    ));
  }, [delivery]);

  if (loading && !data) return <Page title={t('delivery.confirmTitle')} back><Skeleton rows={3} /></Page>;
  if (!delivery) {
    return (
      <Page title={t('delivery.confirmTitle')} back>
        <Empty title={t('delivery.confirmed')} hint="Nothing left to confirm" />
      </Page>
    );
  }

  const total = Object.values(quantities).reduce((s, n) => s + n, 0);
  const mismatch = total !== delivery.driver_declared_quantity;

  async function submit() {
    setBusy(true);
    const body = {
      lines: delivery!.lines.map((l) => ({
        deliveryItemId: l.id,
        confirmedQuantity: quantities[l.id] ?? l.driver_delivered_quantity,
      })),
      raiseDispute: dispute.trim().length > 0 || undefined,
      disputeNote: dispute.trim() || undefined,
    };

    if (!online) {
      await outbox.enqueue({
        method: 'POST',
        path: `/api/deliveries/${delivery!.id}/confirm`,
        body,
        label: `${t('delivery.confirmReceipt')} — ${delivery!.invoice_number}`,
        kind: 'delivery_confirm',
      });
      toast.show(t('app.offlineHint'), 'ok');
      navigate('/home');
      setBusy(false);
      return;
    }

    try {
      const result = await api.post<{ quantityMismatch: boolean; message: string }>(
        `/api/deliveries/${delivery!.id}/confirm`, body,
      );
      toast.show(result.message, result.quantityMismatch ? 'neutral' : 'ok');
      void refetch();
      navigate('/home');
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title={t('delivery.confirmTitle')} subtitle={delivery.invoice_number} back>
      <div className="stack">
        {/* The driver's photo. Showing it makes the confirmation meaningful —
            the retailer is checking against evidence, not memory. */}
        {/*
          AuthImage: a POD photo is protected media, served only to someone
          entitled to see it. The raw tag could never send the token, so the
          "evidence" this screen exists to show was a blank box — and the
          retailer was asked to confirm a delivery against nothing.
        */}
        {delivery.photo_r2_key && (
          <AuthImage
            path={delivery.photo_r2_key}
            alt=""
            style={{ width: '100%', borderRadius: 12, maxHeight: 240, objectFit: 'cover' }}
          />
        )}

        <Card>
          <div className="stack-2">
            <div className="row-between t-sm">
              <span className="t-muted">{t('route.delivered')}</span>
              <span className="t-semi">{timeShort(delivery.delivered_at)}</span>
            </div>
            <div className="row-between t-sm">
              <span className="t-muted">{t('role.driver')}</span>
              <span className="t-semi">{delivery.driver_name}</span>
            </div>
            <div className="row-between t-sm">
              <span className="t-muted">{t('delivery.driverSaid', { n: '' }).replace('  ', ' ')}</span>
              <span className="t-semi">{delivery.driver_declared_quantity}</span>
            </div>
          </div>
        </Card>

        <Card flush>
          <div className="card__header"><h3>{t('delivery.howMany')}</h3></div>
          <div className="list">
            {delivery.lines.map((line) => (
              <div key={line.id} className="list__item list__item--static">
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="t-sm t-semi t-clip">{line.product_name}</div>
                  <div className="t-xs t-muted">
                    Invoiced {line.invoiced_quantity} · driver recorded {line.driver_delivered_quantity}
                    {line.driver_shortage_quantity > 0 && ` · ${line.driver_shortage_quantity} short`}
                  </div>
                </div>
                <Stepper
                  value={quantities[line.id] ?? line.driver_delivered_quantity}
                  onChange={(n) => setQuantities((prev) => ({ ...prev, [line.id]: n }))}
                  min={0}
                  max={line.invoiced_quantity}
                />
              </div>
            ))}
          </div>
        </Card>

        {mismatch && (
          <Banner tone="warn" title="Different from the driver's count">
            <span className="t-sm">
              You are confirming {total}, the driver recorded {delivery.driver_declared_quantity}.
              Our team will review it.
            </span>
          </Banner>
        )}

        <TextArea
          label={t('delivery.raiseDispute')}
          value={dispute}
          onChange={(e) => setDispute(e.target.value)}
          maxLength={500}
        />
      </div>

      <div className="actionbar">
        <Button block size="lg" busy={busy} onClick={() => void submit()}>
          {t('delivery.confirmReceipt')}
        </Button>
      </div>
    </Page>
  );
}
